import { useCallback, useRef, useState } from 'react';
import useSWR from 'swr';

import { backendClient } from '@/lib/api-client';

export interface DictationLanguageOption {
  /** BCP-47 locale code, e.g. "fr-FR". */
  code: string;
  /** Human-readable language + region, e.g. "French (France)". */
  label: string;
}

export interface DictationLanguagesResponse {
  /** Region-pinned default locale, always used and not removable. */
  default: string;
  /** Human-readable label for the default locale. */
  default_label: string;
  /** Additional locale codes the user has enabled (excludes the default). */
  selected: string[];
  /** Selectable additional locales (excludes the default). */
  available: DictationLanguageOption[];
  /** Maximum number of additional languages that can be added. */
  max_additional: number;
}

const DICTATION_LANGUAGES_KEY = '/user/me/dictation-languages';

export const useDictationLanguages = () => {
  const { data, error, isLoading, mutate } = useSWR<DictationLanguagesResponse>(
    DICTATION_LANGUAGES_KEY,
    async (url) => {
      const response = await backendClient.get(url);
      return response.data as DictationLanguagesResponse;
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000,
    },
  );

  const [isSaving, setIsSaving] = useState(false);

  // Serialize saves: each PATCH replaces the full selection, so overlapping requests
  // could otherwise land (or resolve into SWR) out of order and drop a just-added
  // language. Chaining guarantees the backend and the cache both end on the last pick.
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const pendingSavesRef = useRef(0);
  // Id of the most recently queued save. Only that save publishes its response to the
  // cache, so an earlier (still in-flight) save can't momentarily revert the UI to a
  // stale selection as it resolves.
  const latestSaveIdRef = useRef(0);

  const save = useCallback(
    (languages: string[]) => {
      const saveId = ++latestSaveIdRef.current;
      pendingSavesRef.current += 1;
      setIsSaving(true);

      const run = async () => {
        const response = await backendClient.patch(DICTATION_LANGUAGES_KEY, { languages });
        const updated = response.data as DictationLanguagesResponse;
        // Skip intermediate results: only the last-queued save reflects the user's
        // final selection, so publishing earlier ones just causes a flicker back.
        if (saveId === latestSaveIdRef.current) {
          await mutate(updated, { revalidate: false });
        }
        return updated;
      };

      // Run after the previous save settles (success or failure) to preserve order.
      const result = saveChainRef.current.then(run, run);
      saveChainRef.current = result.then(
        () => undefined,
        () => undefined,
      );
      void saveChainRef.current.finally(() => {
        pendingSavesRef.current -= 1;
        if (pendingSavesRef.current === 0) setIsSaving(false);
      });
      return result;
    },
    [mutate],
  );

  const refetch = useCallback(async () => {
    try {
      const result = await mutate();
      return { data: result, error: undefined };
    } catch (err) {
      return { data: undefined, error: err };
    }
  }, [mutate]);

  return {
    data,
    isLoading,
    isError: Boolean(error),
    error,
    refetch,
    save,
    isSaving,
  };
};
